import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ArrowRight, Lock, Volume2, VolumeX } from "lucide-react";

export default function AudioGateRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  onContinue,
  hideStem = false,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const audioCfg = item?.body?.audio || {};
  const seg = item?.body?.segment || {};
  const mustListen = item?.body?.mustListen !== false;

  const url = audioCfg?.url || "";
  const allowPause = !!audioCfg?.allowPause;

  const startSec = Number.isFinite(seg.startSec)
    ? Math.max(0, seg.startSec)
    : 0;
  const endSec =
    Number.isFinite(seg.endSec) && seg.endSec > startSec ? seg.endSec : null;

  const audioRef = useRef(null);
  const frameRef = useRef(0);
  const [completed, setCompleted] = useState(!!initialAnswers?.completed);
  const [progress, setProgress] = useState(0);
  const maxHeardRef = useRef(0);
  const [autoplayNeeded, setAutoplayNeeded] = useState(false);
  const [duration, setDuration] = useState(0);

  // ▼▼▼ NEW: Volume state
  const [volume, setVolume] = useState(1); // 0..1
  const [muted, setMuted] = useState(false);
  // ▲▲▲

  const totalWindow = endSec ? endSec - startSec : null;

  const markComplete = (msListened) => {
    if (completed) return;
    setCompleted(true);
    onAnswerChange?.({ completed: true, listenedMs: msListened });
  };

  const enforceGuards = (el) => {
    const ct = el.currentTime || 0;
    if (ct > maxHeardRef.current + 0.5) el.currentTime = maxHeardRef.current;
    if (endSec && ct > endSec)
      el.currentTime = Math.min(maxHeardRef.current, endSec);
    if (ct < startSec) el.currentTime = startSec;
    maxHeardRef.current = Math.max(maxHeardRef.current, ct);
  };

  const tick = () => {
    const el = audioRef.current;
    if (el) {
      enforceGuards(el);
      const ct = el.currentTime || 0;
      let base = startSec ? Math.max(0, ct - startSec) : ct;
      let denom = totalWindow ?? (el.duration || 0);
      let p = denom > 0 ? Math.min(1, Math.max(0, base / denom)) : 0;
      setProgress(p);

      if (endSec && ct >= endSec - 0.08) {
        el.pause();
        markComplete((maxHeardRef.current - startSec) * 1000);
      } else if (!endSec && el.duration && Math.abs(ct - el.duration) < 0.12) {
        el.pause();
        markComplete(maxHeardRef.current * 1000);
      }
    }
    frameRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onLoaded = () => {
      if (startSec) el.currentTime = startSec;
      setDuration(endSec ? endSec - startSec : el.duration || 0);
      // sync initial volume/mute
      el.volume = volume;
      el.muted = muted;

      el.play().catch(() => setAutoplayNeeded(true));
    };

    el.addEventListener("loadedmetadata", onLoaded);
    frameRef.current = requestAnimationFrame(tick);

    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSec, endSec, url]);

  const onSeeking = (e) => enforceGuards(e.target);

  const acknowledgeAutoplay = () => {
    const el = audioRef.current;
    if (!el) return;
    el.play()
      .then(() => setAutoplayNeeded(false))
      .catch(() => {});
  };

  const fmt = (sec) => {
    if (!Number.isFinite(sec)) return "--:--";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const playedSec = (totalWindow ?? duration) * progress;

  // ▼▼▼ NEW: handlers to sync volume/mute to the hidden audio element
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const toggleMute = () => setMuted((m) => !m);
  const onVolumeChange = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (v > 0 && muted) setMuted(false);
    if (v === 0 && !muted) setMuted(true);
  };
  // ▲▲▲

  return (
    <div className="rounded-lg bg-white p-4">
      {!hideStem && (
        <div
          dir={isRtl ? "rtl" : "ltr"}
          className="prose max-w-none text-gray-800 mb-4"
          dangerouslySetInnerHTML={{
            __html:
              item?.stem?.fa ||
              item?.stem?.en ||
              t("audio_gate.listen", "Please listen to this part."),
          }}
        />
      )}

      <div className="relative rounded-md border bg-gray-50 p-4 overflow-hidden">
        {/* hidden audio */}
        <audio
          ref={audioRef}
          src={url}
          autoPlay
          playsInline
          preload="auto"
          controls={false}
          onSeeking={onSeeking}
          style={{ display: "none" }}
        />

        {autoplayNeeded && (
          <button
            onClick={acknowledgeAutoplay}
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
              {t("audio_gate.tap_to_start", "Tap to allow audio")}
            </span>
          </button>
        )}

        {/* Progress bar */}
        <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full bg-emerald-600 transition-[width] duration-150 ease-linear"
            style={{ width: `${Math.round(progress * 100)}%` }}
            aria-label={t("audio_gate.progress", "Playback progress")}
          />
        </div>

        {/* times + lock + VOLUME CONTROL */}
        <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-600">
          <span>{fmt((startSec || 0) + playedSec)}</span>

          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {t("audio_gate.locked", "Controls hidden")}
            </span>

            {/* ▼▼▼ NEW: Volume control (speaker + slider) */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={
                  muted
                    ? t("audio_gate.unmute", "Unmute")
                    : t("audio_gate.mute", "Mute")
                }
                className="p-1 rounded hover:bg-white/60"
              >
                {muted || volume === 0 ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={muted ? 0 : volume}
                onChange={onVolumeChange}
                className="w-28 accent-emerald-600"
                aria-label={t("audio_gate.volume", "Volume")}
              />
            </div>
            {/* ▲▲▲ */}
          </div>

          <span>{fmt((startSec || 0) + (totalWindow ?? duration))}</span>
        </div>

        <div className="mt-3 text-center text-sm">
          {completed ? (
            <span className="inline-flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              {t("audio_gate.completed", "Completed. You can continue.")}
            </span>
          ) : mustListen ? (
            <span className="text-gray-600">
              {t(
                "audio_gate.must_listen",
                "You must listen completely to proceed."
              )}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-4 text-center">
        <button
          onClick={onContinue}
          disabled={!completed && mustListen}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
            !completed && mustListen
              ? "border opacity-50 cursor-not-allowed"
              : "bg-emerald-600 text-white hover:bg-emerald-700"
          }`}
        >
          {t("common.continue", "Continue")} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
